/**
 * Model for Carousel components in Cypress tests
 */

type CarouselData = {
  scrollPosition: number
  isScrollable: boolean
  isLeftArrowVisible: boolean
  isRightArrowVisible: boolean
  scrollRef: React.MutableRefObject<HTMLDivElement | null>
  scrollLeft: (pixels?: number) => void
  scrollRight: (pixels?: number) => void
}

type CarouselModel = {
  [carouselId: string]: CarouselData | undefined
}

class Carousel {
  [carouselId: string]: CarouselData | any

  constructor(model: CarouselModel) {
    Object.assign(this, model)

    // Bind methods to the instance
    this.canScrollRight = this.canScrollRight.bind(this)
    this.canScrollLeft = this.canScrollLeft.bind(this)
    this.getScrollPosition = this.getScrollPosition.bind(this)
    this.scrollRightCarousel = this.scrollRightCarousel.bind(this)
    this.scrollLeftCarousel = this.scrollLeftCarousel.bind(this)
  }

  /**
   * Check if a specific carousel can be scrolled right
   *
   * @param carouselId The ID of the carousel
   * @returns Boolean indicating if it's possible to scroll right
   */
  canScrollRight(carouselId: string): boolean {
    const carousel = this[carouselId]
    if (!carousel) return false

    return carousel.isRightArrowVisible
  }

  /**
   * Check if a specific carousel can be scrolled left
   *
   * @param carouselId The ID of the carousel
   * @returns Boolean indicating if it's possible to scroll left
   */
  canScrollLeft(carouselId: string): boolean {
    const carousel = this[carouselId]
    if (!carousel) return false

    return carousel.isLeftArrowVisible
  }

  /**
   * Get the current scroll position of a carousel
   *
   * @param carouselId The ID of the carousel
   * @returns The current scroll position
   */
  getScrollPosition(carouselId: string): number {
    const carousel = this[carouselId]
    if (!carousel) return 0

    return carousel.scrollPosition
  }

  /**
   * Scroll a carousel to the right by a specified amount
   *
   * @param carouselId The ID of the carousel
   * @param pixels Optional number of pixels to scroll
   */
  scrollRightCarousel(carouselId: string, pixels?: number): void {
    const carousel = this[carouselId]
    if (!carousel || !carousel.scrollRight) return

    carousel.scrollRight(pixels)
  }

  /**
   * Scroll a carousel to the left by a specified amount
   *
   * @param carouselId The ID of the carousel
   * @param pixels Optional number of pixels to scroll
   */
  scrollLeftCarousel(carouselId: string, pixels?: number): void {
    const carousel = this[carouselId]
    if (!carousel || !carousel.scrollLeft) return

    carousel.scrollLeft(pixels)
  }
}

export default Carousel
